import React, { useContext, useEffect, useState } from "react";
import { useMutation } from "react-query";
import GlobalContext from "src/context/global-context";
import {
  APISomethingWrongMsg,
  NetworkDisconnectMsg,
  permissions as permissionConstants,
} from "src/lib/constants";
import usePermissions from "src/hooks/use-permissions";
import { getErrorMessage, logger } from "src/lib/utils";
import client from "../../api";
import { Table, DatePicker } from "antd";
import moment from "moment";

const { RangePicker } = DatePicker;

type SearchType =
  | "card_id"
  | "search"
  | "user_id"
  | "package_type"
  | "card_type";

const packageTypeOptions = [
  {
    value: "MONTHLY",
    label: "MONTHLY",
  },
  {
    value: "YEARLY",
    label: "YEARLY",
  },
];

const CardBillings = () => {
  const { canView, canList } = usePermissions(
    permissionConstants.CARD_BILLINGS
  );
  const canViewBillings = canView || canList;

  const [dataSource, setDataSource] = useState<any[]>([]);

  const [searchType, setSearchType] = useState<SearchType>("card_id");
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [dateRange, setDateRange] = useState<any>(null);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [products, setProducts] = useState<any[]>([]);

  const { setIsAlertOpened, setAlertText } = useContext(GlobalContext);

  // LIST API
  const { mutate: getCardBillingsList, isLoading } = useMutation(
    client.cardBillings?.list,
    {
      onSuccess: (res: any) => {
        const response = res?.data;

        setDataSource(
          (response?.data || []).map((item: any) => ({
            ...item,
            key: item.id,
          })),
        );

        setTotal(response?.total || 0);
      },

      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }

        setIsAlertOpened(true);
      },
    },
  );

  // Filter dropdown list
  const { mutate: getProducts } = useMutation(client.cardProducts.list, {
    onSuccess: (res: any) => {
      logger.log("Products", res);

      setProducts(res?.data?.data || []);
    },
  });

  useEffect(() => {
    if (canViewBillings) {
      getProducts({});
    }
  }, [canViewBillings]);
  
  // Debounce search
  useEffect(() => {
    if (!searchValue) {
      setDebouncedSearch("");
      return;
    }
    const timer = setTimeout(() => {
      setDebouncedSearch(searchValue);
      setPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchValue]);

  // Fetch list
  useEffect(() => {
    if (!canViewBillings) return;
    const params: any = {
      page,
      limit,
    };

    if (debouncedSearch) {
      params[searchType] = debouncedSearch;
    }

    if (dateRange?.length === 2) {
      params.start_date = dateRange[0].format("YYYY-MM-DD");
      params.end_date = dateRange[1].format("YYYY-MM-DD");
    }

    getCardBillingsList(params);
  }, [canViewBillings, page, limit, debouncedSearch, searchType, dateRange]);

  const columns: any[] = [
    {
      title: "ID",
      dataIndex: "id",
      width: 70,
    },

    {
      title: "Card ID",
      dataIndex: "card_id",
      width: 100,
    },

    {
      title: "Masked Card Number",
      dataIndex: ["card", "masked_card_number"],
      width: 180,
      render: (value: string) => value || "-",
    },

    {
      title: "Product Name",
      dataIndex: ["card", "product", "name"],
      width: 160,
      render: (value: string) => value || "-",
    },

    {
      title: "User ID",
      dataIndex: "user_id",
      width: 100,
    },

    {
      title: "Package",
      dataIndex: "package_type",
      width: 120,
    },

    {
      title: "Fee",
      dataIndex: "fee_amount",
      width: 100,
      render: (value: string) => value || "-",
    },

    {
      title: "Billing Day",
      dataIndex: "billing_day",
      width: 120,
    },

    {
      title: "Next Charge",
      dataIndex: "next_charge_date",
      width: 140,
      render: (value: string) =>
        value ? moment(value).format("YYYY-MM-DD") : "-",
    },

    {
      title: "Last Attempt",
      dataIndex: "last_attempt_date",
      width: 140,
      render: (value: string) =>
        value ? moment(value).format("YYYY-MM-DD") : "-",
    },

    {
      title: "Periods Charged",
      dataIndex: "periods_charged",
      width: 150,
    },

    {
      title: "Unpaid Amount",
      dataIndex: "unpaid_amount",
      width: 140,
    },

    {
      title: "Last Charge Status",
      dataIndex: "last_charge_status",
      width: 180,
      render: (value: string) => value || "-",
    },

    {
      title: "Status",
      dataIndex: "status",
      width: 120,
      render: (value: string) => (
        <span
          className={
            value === "ACTIVE"
              ? "text-green-600 font-medium"
              : "text-red-500 font-medium"
          }
        >
          {value || "-"}
        </span>
      ),
    },

    {
      title: "Created",
      dataIndex: "created_at",
      width: 170,
      render: (value: string) =>
        value ? moment(value).format("YYYY-MM-DD HH:mm") : "-",
    },
    {
      title: "Card  Expiry",
      dataIndex: ["card", "expiry_date"],
      width: 160,
      render: (value: string) => {
        if (!value || value.length !== 6) return "-";
        const year = value.slice(0, 4);
        const month = value.slice(4, 6);
        return `${month}/${year}`;
      },
    },
  ];

  return (
    <div className="p-6 bg-white rounded-lg">
      <div className="flex justify-between items-center mb-5">
        <h2 className="text-xl font-semibold">Card Billings</h2>

        <div className="flex flex-wrap items-center gap-4">
          {/* Search Type */}
          <select
            value={searchType}
            onChange={(e) => {
              const newSearchType = e.target.value as SearchType;
              setSearchType(newSearchType);
              setSearchValue("");
              setDebouncedSearch("");
              setPage(1);
            }}
            className="h-11 w-[220px] rounded-lg border border-gray-300 px-4 outline-none"
          >
            <option value="card_id">Card ID</option>
            <option value="search">Card Last 4</option>
            <option value="user_id">User ID</option>
            <option value="package_type">Package type</option>
            <option value="card_type">Card Type</option>
          </select>

          {/* Search Value */}
          {/* Search Value / Package Type */}
          {/* Search Value / Package Type / Card Type */}
          {searchType === "package_type" ? (
            <select
              value={searchValue}
              onChange={(e) => {
                setSearchValue(e.target.value);
                setPage(1);
              }}
              className="h-11 w-[280px] rounded-lg border border-gray-300 px-4 outline-none"
            >
              <option value="">Select Package Type</option>

              {packageTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          ) : searchType === "card_type" ? (
            <select
              value={searchValue}
              onChange={(e) => {
                setSearchValue(e.target.value);
                setPage(1);
              }}
              className="h-11 w-[280px] rounded-lg border border-gray-300 px-4 outline-none"
            >
              <option value="">Select Card Type</option>

              {products.map((item: any) => (
                <option key={item.id} value={item.name}>
                  {item.name}
                </option>
              ))}
            </select>
          ) : (
            <input
              value={searchValue}
              onChange={(e) => {
                const value =
                  searchType === "search"
                    ? e.target.value.replace(/\D/g, "").slice(0, 4)
                    : e.target.value;

                setSearchValue(value);
              }}
              type={searchType === "user_id" ? "number" : "text"}
              placeholder={
                searchType === "card_id"
                  ? "Enter Card ID"
                  : searchType === "search"
                    ? "Enter Last 4 Digits"
                    : searchType === "user_id"
                      ? "Enter User ID"
                      : "Search"
              }
              maxLength={searchType === "search" ? 4 : undefined}
              className="h-11 w-[280px] rounded-lg border border-gray-300 px-4 outline-none"
            />
          )}

          {/* Date Filter */}
          <RangePicker
            value={dateRange}
            onChange={(dates) => {
              setDateRange(dates);
              setPage(1);
            }}
            className="!h-11"
            format="YYYY-MM-DD"
          />
        </div>
      </div>

      {!canViewBillings ? (
        <div className="flex flex-col items-center justify-center bg-white rounded-[12px] p-8 mt-2 sm:mt-4 text-center shadow-sm">
          <p className="font-manrope text-[16px] font-semibold text-secondary">
            You do not have permission to view card billings.
          </p>
        </div>
      ) : (
        <div className="custom-scroll flex flex-col w-full bg-white rounded-[12px] p-4 mt-2 sm:mt-4">
          <Table
            rowKey="id"
            loading={isLoading}
            columns={columns}
            dataSource={dataSource}
            scroll={{ x: 1600 }}
            pagination={{
              current: page,
              pageSize: limit,
              total,
              position: ["bottomCenter"],
              showSizeChanger: false,
              onChange: (newPage, newLimit) => {
                setPage(newPage);
                setLimit(newLimit);
              },
            }}
          />
        </div>
      )}
    </div>
  );
};

export default CardBillings;
